import React, { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import ListaPremium, { iniciaisDe } from "../components/ListaPremium";
import { useAuth } from "../context/AuthContext";
import { Button, Field, Input, Select, Textarea, Tag, Modal, tomDoStatus, dataBR, paraInputData, brl } from "../components/ui";

const FORM_VAZIO = {
    cod_contrato: "",
    data_pag: "",
    data_vencimento: "",
    descricao: "",
    status_pag: "",
    metodo: "",
    valorPago: ""
};

const STATUS_PAG = ["concluido", "em andamento", "em atraso"];
const METODOS = ["pix", "especie", "parcelado", "boleto", "credito", "debito"];

/* ----------------------------------------------------------------------
   Parcelamento — prévia na tela
   As parcelas são realmente criadas pelo servidor (POST /pagamentos/parcelado),
   que é a autoridade sobre valores e datas. As funções abaixo repetem a mesma
   regra só para mostrar a prévia antes de salvar: divisão em centavos, com o
   resto distribuído um centavo por parcela, e vencimento mensal que cai no
   último dia do mês quando o dia não existe (31/01 → 28/02).
   ---------------------------------------------------------------------- */

const MAX_PARCELAS = 60;

function dividirEmParcelas(total, n) {
    const centavos = Math.round(Number(total) * 100);
    const base = Math.floor(centavos / n);
    const sobra = centavos - base * n;
    return Array.from({ length: n }, (_, i) => (base + (i < sobra ? 1 : 0)) / 100);
}

function somarMeses(data, meses) {
    const [ano, mes, dia] = String(data).split("-").map(Number);
    const total = ano * 12 + (mes - 1) + meses;
    const anoFinal = Math.floor(total / 12);
    const mesFinal = (total % 12) + 1;
    const ultimoDia = new Date(Date.UTC(anoFinal, mesFinal, 0)).getUTCDate();
    const diaFinal = Math.min(dia, ultimoDia);
    return `${anoFinal}-${String(mesFinal).padStart(2, "0")}-${String(diaFinal).padStart(2, "0")}`;
}

const PARCELAMENTO_VAZIO = {
    parcelas: 10,
    valor_total: "",
    data_primeiro_vencimento: "",
    metodo: "boleto",
    descricao: ""
};

const Pagamentos = () => {
    const { podeEditar } = useAuth();
    const [busca, setBusca] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
    const [filtro, setFiltro] = useState("todos");
    const [resumo, setResumo] = useState([]);
    const [contratosAll, setContratosAll] = useState([]);
    const [mostrarInfo, setMostrarInfo] = useState(false);
    const [mostrarInfoPag, setMostrarInfoPag] = useState(false);
    const [mostrarContrato, setMostrarContrato] = useState(false);
    const [mostrarAdd, setMostrarAdd] = useState(false);
    const [mostrarEdit, setMostrarEdit] = useState(false);
    const [pagamentoSelecionado, setPagamentoSelecionado] = useState(null);
    const [contratoSelecionado, setContratoSelecionado] = useState(null);
    const [listaPagamentos, setListaPagamentos] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [formData, setFormData] = useState(FORM_VAZIO);
    const [modoParcelado, setModoParcelado] = useState(false);
    const [parcelamento, setParcelamento] = useState(PARCELAMENTO_VAZIO);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const carregarResumo = useCallback(async () => {
        try {
            const response = await api.get("/pagamentos");
            setResumo(response.data);
        } catch (error) {
            console.error("Erro ao buscar pagamentos:", error);
        }
    }, []);

    const carregarPagamentos = useCallback(async (cod) => {
        if (!cod) return;
        try {
            const response = await api.get(`/pagamentos/${cod}`);
            setListaPagamentos(response.data);
        } catch (error) {
            console.error("Erro ao buscar pagamentos:", error);
        }
    }, []);

    useEffect(() => {
        carregarResumo();
        api.get("/contratos").then((r) => setContratosAll(r.data)).catch(() => {});
    }, [carregarResumo]);

    useEffect(() => {
        if (contratoSelecionado) {
            carregarPagamentos(contratoSelecionado.cod_cont || contratoSelecionado.cod_contrato);
        }
    }, [contratoSelecionado, carregarPagamentos]);

    const criarPagamento = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            const cod = contratoSelecionado.cod_cont || contratoSelecionado.cod_contrato;
            await api.post("/pagamentos", { ...formData, cod_contrato: cod });
            carregarResumo();
            carregarPagamentos(cod);
            setMostrarAdd(false);
            setFormData(FORM_VAZIO);
        } catch (error) {
            setErro(error.response?.data?.error || error.message);
        } finally {
            setCarregando(false);
        }
    };

    const mudarParcelamento = (campo, valor) =>
        setParcelamento((prev) => ({ ...prev, [campo]: valor }));

    // Prévia das parcelas: recalculada a cada digitação, sem ir ao servidor.
    // Aceita valor com vírgula (5.000,50) além do ponto.
    const previaParcelas = React.useMemo(() => {
        const total = Number(String(parcelamento.valor_total).replace(/\./g, "").replace(",", "."));
        const n = Number(parcelamento.parcelas);
        if (!total || total <= 0 || !parcelamento.data_primeiro_vencimento) return [];
        if (!Number.isInteger(n) || n < 2 || n > MAX_PARCELAS) return [];
        if (Math.round(total * 100) < n) return [];
        return dividirEmParcelas(total, n).map((valor, i) => ({
            numero: i + 1,
            vencimento: somarMeses(parcelamento.data_primeiro_vencimento, i),
            valor
        }));
    }, [parcelamento.valor_total, parcelamento.parcelas, parcelamento.data_primeiro_vencimento]);

    const criarParcelamento = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            const cod = contratoSelecionado.cod_cont || contratoSelecionado.cod_contrato;
            const total = Number(String(parcelamento.valor_total).replace(/\./g, "").replace(",", "."));
            const { data } = await api.post("/pagamentos/parcelado", {
                ...parcelamento,
                valor_total: total,
                cod_contrato: cod
            });
            carregarResumo();
            carregarPagamentos(cod);
            setMostrarAdd(false);
            setParcelamento(PARCELAMENTO_VAZIO);
            setModoParcelado(false);
            setMostrarInfo(true);
            alert(data.message);
        } catch (error) {
            setErro(error.response?.data?.error || error.message);
        } finally {
            setCarregando(false);
        }
    };

    const atualizarPagamento = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            await api.put(`/pagamentos/${pagamentoSelecionado.cod_pag}`, pagamentoSelecionado);
            carregarResumo();
            carregarPagamentos(pagamentoSelecionado.cod_contrato);
            setMostrarEdit(false);
            setMostrarInfoPag(true);
        } catch (error) {
            setErro(error.response?.data?.error || error.message);
        } finally {
            setCarregando(false);
        }
    };

    const deletarPagamento = async (cod_pag) => {
        if (!window.confirm("Excluir este pagamento?")) return;
        try {
            await api.delete(`/pagamentos/${cod_pag}`);
            carregarResumo();
            if (contratoSelecionado) carregarPagamentos(contratoSelecionado.cod_cont || contratoSelecionado.cod_contrato);
            setMostrarInfoPag(false);
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível excluir o pagamento.");
        }
    };

    // Totais do escritório (para as tiles do topo)
    const totais = resumo.reduce((t, c) => ({
        valor: t.valor + Number(c.valor || 0),
        pago: t.pago + Number(c.valor_pago || 0),
        receber: t.receber + Math.max(0, Number(c.faltante || 0))
    }), { valor: 0, pago: 0, receber: 0 });

    const resumoFiltrado = resumo.filter((c) => {
        const q = busca.toLowerCase();
        const casaBusca = (
            (c.nome_cliente || "").toLowerCase().includes(q) ||
            (c.status_contrato || "").toLowerCase().includes(q) ||
            (c.tipo_servico || "").toLowerCase().includes(q) ||
            (c.status_pag || "").toLowerCase().includes(q)
        );
        const quitado = Number(c.faltante) <= 0;
        const casaFiltro =
            filtro === "todos" ? true :
            filtro === "pendentes" ? !quitado :
            filtro === "quitados" ? quitado : true;
        return casaBusca && casaFiltro;
    });

    const contarFiltro = (f) => resumo.filter((c) => {
        const quitado = Number(c.faltante) <= 0;
        return f === "todos" ? true : f === "pendentes" ? !quitado : quitado;
    }).length;

    return (
        <div>
            <Header />

            <main className="page">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Pagamentos</h2>
                    <div className="toolbar-spacer">
                        <Input placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
                        {podeEditar && <Button onClick={() => { setFormData(FORM_VAZIO); setParcelamento(PARCELAMENTO_VAZIO); setModoParcelado(false); setErro(null); setMostrarContrato(true); }}>Novo pagamento</Button>}
                    </div>
                </div>

                {/* Totais do escritório */}
                <div className="fin-tiles">
                    <div className="fin-tile">
                        <span className="fin-tile-rot">A receber</span>
                        <span className="fin-tile-val" style={{ color: totais.receber > 0 ? "#8c2418" : "var(--color-text)" }}>{brl(totais.receber)}</span>
                        <span className="fin-tile-apoio">saldo em aberto</span>
                    </div>
                    <div className="fin-tile">
                        <span className="fin-tile-rot">Recebido</span>
                        <span className="fin-tile-val" style={{ color: "#3d6321" }}>{brl(totais.pago)}</span>
                        <span className="fin-tile-apoio">já pago pelos clientes</span>
                    </div>
                    <div className="fin-tile">
                        <span className="fin-tile-rot">Em contratos</span>
                        <span className="fin-tile-val">{brl(totais.valor)}</span>
                        <span className="fin-tile-apoio">{totais.valor > 0 ? `${Math.round(totais.pago / totais.valor * 100)}% recebido` : "—"}</span>
                    </div>
                </div>

                {/* Filtros */}
                <div className="chips-filtro">
                    {[["todos", "Todos"], ["pendentes", "Pendentes"], ["quitados", "Quitados"]].map(([f, rot]) => (
                        <button key={f} className={`chip-filtro ${filtro === f ? "ativo" : ""}`} onClick={() => setFiltro(f)}>
                            {rot} <span className="chip-contador">{contarFiltro(f)}</span>
                        </button>
                    ))}
                </div>

                <ListaPremium
                    vazio="Nenhum contrato encontrado."
                    itens={resumoFiltrado.map((c) => {
                        const quitado = Number(c.faltante) <= 0;
                        const frac = Number(c.valor) > 0 ? Number(c.valor_pago) / Number(c.valor) : 0;
                        return {
                            chave: c.cod_cont,
                            iniciais: iniciaisDe(c.nome_cliente),
                            inicialTom: quitado ? "success" : Number(c.valor_pago) > 0 ? "accent" : "danger",
                            titulo: c.nome_cliente,
                            subtitulo: [
                                `Contrato ${c.cod_cont}`,
                                `pago ${brl(c.valor_pago)} de ${brl(c.valor)}`,
                                Number(c.valor_aberto) > 0 ? `${brl(c.valor_aberto)} em parcelas a vencer` : null
                            ].filter(Boolean).join(" · "),
                            progresso: frac,
                            progressoTom: quitado ? "success" : Number(c.valor_pago) > 0 ? "accent" : "danger",
                            status: c.status_pag || "sem pagamento",
                            direita: quitado
                                ? <span style={{ color: "#3d6321" }}>quitado</span>
                                : <span style={{ color: "#8c2418" }}>falta {brl(c.faltante)}</span>,
                            aoClicar: () => { setMostrarInfo(true); setContratoSelecionado(c); }
                        };
                    })}
                />

                {mostrarInfo && contratoSelecionado && (
                    <Modal titulo={`Pagamentos · ${contratoSelecionado.nome_cliente}`} largo onFechar={() => setMostrarInfo(false)}>
                        <div className="scroll-x">
                            <table className="table">
                                <thead>
                                    <tr><th>Cód.</th><th>Pagamento</th><th>Vencimento</th><th>Status</th><th>Método</th><th className="num">Valor</th></tr>
                                </thead>
                                <tbody>
                                    {listaPagamentos.map((p) => (
                                        <tr key={p.cod_pag} className="clicavel" onClick={() => { setMostrarInfo(false); setMostrarInfoPag(true); setPagamentoSelecionado(p); }}>
                                            <td className="num">{p.cod_pag}</td>
                                            <td className="num">{dataBR(p.data_pag) || "—"}</td>
                                            <td className="num">{dataBR(p.data_vencimento)}</td>
                                            <td><Tag tom={tomDoStatus(p.status_pag)}>{p.status_pag}</Tag></td>
                                            <td>{p.metodo}</td>
                                            <td className="num">{brl(p.valorPago)}</td>
                                        </tr>
                                    ))}
                                    {listaPagamentos.length === 0 && <tr><td colSpan="6" className="text-muted">Nenhum pagamento registrado.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <div className="modal-acoes">
                            <Button variante="secondary" onClick={() => setMostrarInfo(false)}>Voltar</Button>
                            {podeEditar && <Button onClick={() => { setMostrarInfo(false); setMostrarAdd(true); }}>Novo pagamento</Button>}
                        </div>
                    </Modal>
                )}

                {mostrarInfoPag && pagamentoSelecionado && (
                    <Modal titulo={`Pagamento ${pagamentoSelecionado.cod_pag}`} onFechar={() => { setMostrarInfoPag(false); setMostrarInfo(true); }}>
                        <div className="grid-form">
                            <Field label="Pagamento"><Input value={dataBR(pagamentoSelecionado.data_pag) || "—"} readOnly /></Field>
                            <Field label="Vencimento"><Input value={dataBR(pagamentoSelecionado.data_vencimento)} readOnly /></Field>
                            <Field label="Status"><Input value={pagamentoSelecionado.status_pag} readOnly /></Field>
                            <Field label="Método"><Input value={pagamentoSelecionado.metodo} readOnly /></Field>
                            <Field label="Valor pago" className="span-2"><Input value={brl(pagamentoSelecionado.valorPago)} readOnly /></Field>
                            <Field label="Descrição" className="span-2"><Textarea value={pagamentoSelecionado.descricao || ""} readOnly /></Field>
                        </div>
                        <div className="modal-acoes">
                            {podeEditar && <Button variante="danger" onClick={() => deletarPagamento(pagamentoSelecionado.cod_pag)}>Excluir</Button>}
                            <Button variante="secondary" onClick={() => { setMostrarInfoPag(false); setMostrarInfo(true); }}>Voltar</Button>
                            {podeEditar && <Button onClick={() => { setErro(null); setMostrarEdit(true); setMostrarInfoPag(false); }}>Editar</Button>}
                        </div>
                    </Modal>
                )}

                {mostrarContrato && (
                    <Modal titulo="Escolha o contrato" largo onFechar={() => setMostrarContrato(false)}>
                        <div className="scroll-x">
                            <table className="table">
                                <thead><tr><th>Cód.</th><th>Cliente</th><th>Status</th><th>Tipo</th><th className="num">Valor</th></tr></thead>
                                <tbody>
                                    {contratosAll.map((ct) => (
                                        <tr key={ct.cod_contrato} className="clicavel" onClick={() => { setContratoSelecionado(ct); setMostrarContrato(false); setMostrarAdd(true); }}>
                                            <td className="num">{ct.cod_contrato}</td>
                                            <td>{ct.nome_cliente}</td>
                                            <td><Tag tom={tomDoStatus(ct.status_contrato)}>{ct.status_contrato}</Tag></td>
                                            <td>{ct.tipo_servico}</td>
                                            <td className="num">{brl(ct.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="modal-acoes">
                            <Button variante="secondary" onClick={() => setMostrarContrato(false)}>Voltar</Button>
                        </div>
                    </Modal>
                )}

                {mostrarAdd && contratoSelecionado && (
                    <Modal titulo="Novo pagamento" onFechar={() => setMostrarAdd(false)}>
                        {/* Único x parcelado: no parcelado o servidor cria todas as
                            parcelas de uma vez, uma por mês. */}
                        <div className="nav-pilula" style={{ marginBottom: 4 }}>
                            <button type="button" className={!modoParcelado ? "ativo" : ""} onClick={() => { setModoParcelado(false); setErro(null); }}>
                                Pagamento único
                            </button>
                            <button type="button" className={modoParcelado ? "ativo" : ""} onClick={() => { setModoParcelado(true); setErro(null); }}>
                                Parcelado
                            </button>
                        </div>

                        {modoParcelado ? (
                            <form onSubmit={criarParcelamento} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <div className="grid-form">
                                    <Field label="Valor total (R$)">
                                        <Input name="valor_total" required inputMode="decimal" placeholder="5000,00"
                                            value={parcelamento.valor_total}
                                            onChange={(e) => mudarParcelamento("valor_total", e.target.value)} />
                                    </Field>
                                    <Field label="Nº de parcelas">
                                        <Select name="parcelas" value={parcelamento.parcelas}
                                            onChange={(e) => mudarParcelamento("parcelas", Number(e.target.value))}>
                                            {Array.from({ length: MAX_PARCELAS - 1 }, (_, i) => i + 2).map((n) => (
                                                <option key={n} value={n}>{n}x</option>
                                            ))}
                                        </Select>
                                    </Field>
                                    <Field label="1º vencimento">
                                        <Input type="date" name="data_primeiro_vencimento" required
                                            value={parcelamento.data_primeiro_vencimento}
                                            onChange={(e) => mudarParcelamento("data_primeiro_vencimento", e.target.value)} />
                                    </Field>
                                    <Field label="Método">
                                        <Select name="metodo" value={parcelamento.metodo}
                                            onChange={(e) => mudarParcelamento("metodo", e.target.value)}>
                                            {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                                        </Select>
                                    </Field>
                                    <Field label="Descrição (opcional)" className="span-2">
                                        <Input name="descricao" placeholder="Honorários contratuais"
                                            value={parcelamento.descricao}
                                            onChange={(e) => mudarParcelamento("descricao", e.target.value)} />
                                    </Field>
                                </div>

                                {previaParcelas.length > 0 ? (
                                    <div>
                                        <p className="card-kicker" style={{ margin: "0 0 6px" }}>
                                            {previaParcelas.length}x de {brl(previaParcelas[0].valor)}
                                            {previaParcelas[0].valor !== previaParcelas[previaParcelas.length - 1].valor
                                                && ` (última de ${brl(previaParcelas[previaParcelas.length - 1].valor)})`}
                                        </p>
                                        <div className="scroll-x" style={{ maxHeight: 220, overflowY: "auto" }}>
                                            <table className="table">
                                                <thead><tr><th>Parcela</th><th>Vencimento</th><th className="num">Valor</th></tr></thead>
                                                <tbody>
                                                    {previaParcelas.map((p) => (
                                                        <tr key={p.numero}>
                                                            <td className="num">{p.numero}/{previaParcelas.length}</td>
                                                            <td className="num">{dataBR(p.vencimento)}</td>
                                                            <td className="num">{brl(p.valor)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted" style={{ margin: 0, fontSize: 14 }}>
                                        Informe o valor total e o primeiro vencimento para ver as parcelas.
                                    </p>
                                )}

                                <div className="modal-acoes">
                                    {erro && <p className="erro">{erro}</p>}
                                    <Button variante="secondary" onClick={() => { setMostrarAdd(false); setMostrarContrato(true); }}>Voltar</Button>
                                    <Button type="submit" disabled={carregando || previaParcelas.length === 0}>
                                        {carregando ? "Gerando..." : `Gerar ${parcelamento.parcelas} parcelas`}
                                    </Button>
                                </div>
                            </form>
                        ) : (
                        <form onSubmit={criarPagamento} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div className="grid-form">
                                <Field label="Data do pagamento"><Input type="date" name="data_pag" value={formData.data_pag} onChange={handleChange} /></Field>
                                <Field label="Vencimento"><Input type="date" name="data_vencimento" required value={formData.data_vencimento} onChange={handleChange} /></Field>
                                <Field label="Status">
                                    <Select name="status_pag" required value={formData.status_pag} onChange={handleChange}>
                                        <option value="">Selecione</option>
                                        {STATUS_PAG.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Método">
                                    <Select name="metodo" required value={formData.metodo} onChange={handleChange}>
                                        <option value="">Selecione</option>
                                        {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Valor pago (R$)" className="span-2"><Input name="valorPago" required value={formData.valorPago} onChange={handleChange} placeholder="0,00" /></Field>
                                <Field label="Descrição" className="span-2"><Textarea name="descricao" value={formData.descricao} onChange={handleChange} /></Field>
                            </div>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => { setMostrarAdd(false); setMostrarContrato(true); }}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                        )}
                    </Modal>
                )}

                {mostrarEdit && pagamentoSelecionado && (
                    <Modal titulo="Editar pagamento" onFechar={() => setMostrarEdit(false)}>
                        <form onSubmit={atualizarPagamento} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div className="grid-form">
                                <Field label="Data do pagamento">
                                    <Input type="date" value={paraInputData(pagamentoSelecionado.data_pag)} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, data_pag: e.target.value })} />
                                </Field>
                                <Field label="Vencimento">
                                    <Input type="date" value={paraInputData(pagamentoSelecionado.data_vencimento)} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, data_vencimento: e.target.value })} />
                                </Field>
                                <Field label="Status">
                                    <Select value={pagamentoSelecionado.status_pag} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, status_pag: e.target.value })}>
                                        {STATUS_PAG.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Método">
                                    <Select value={pagamentoSelecionado.metodo} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, metodo: e.target.value })}>
                                        {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Valor pago (R$)" className="span-2">
                                    <Input value={pagamentoSelecionado.valorPago} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, valorPago: e.target.value })} />
                                </Field>
                                <Field label="Descrição" className="span-2">
                                    <Textarea value={pagamentoSelecionado.descricao || ""} onChange={(e) => setPagamentoSelecionado({ ...pagamentoSelecionado, descricao: e.target.value })} />
                                </Field>
                            </div>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => { setMostrarEdit(false); setMostrarInfoPag(true); }}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default Pagamentos;
